import { approvalStatus, approvalStatusText, verificationStatus, verificationStatusText, signoffStatus, signoffStatusText, reviewStatus, reviewStatusText, lockStatus, lockStatusText, approveStatusNapi, verifiedStatusNapi, signoffStatusNapi, reviewStatusNapi, lockedStatusNapi } from "@/constants/form";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      participantStatuses: null,
      selectedMilestoneId: null,
      milestone: null,
      loading: false,
      userDetails: null
    };
  },

  computed: {
    ...mapGetters(["user"]),

    isVerified() {
      return this.participantStatuses && this.participantStatuses.verifyStatus === verificationStatus.VERIFIED;
    },

    isApproved() {
      return this.participantStatuses && this.participantStatuses.approveStatus === approvalStatus.APPROVED;
    },

    isSignedOff() {
      return this.participantStatuses && this.participantStatuses.signoffStatus === signoffStatus.SIGNEDOFF;
    },

    isReviewed() {
      return this.participantStatuses && this.participantStatuses.reviewStatus === reviewStatus.REVIEWED;
    },

    isLocked() {
      return this.participantStatuses && this.participantStatuses.lockStatus === lockStatus.LOCKED;
    },

    verifiedStatusText() {
      return this.isVerified ? verificationStatusText.VERIFIED : verificationStatusText.NOT_VERIFIED;
    },

    approvedStatusText() {
      return this.isApproved ? approvalStatusText.APPROVED : approvalStatusText.NOT_APPROVED;
    },

    signedOffStatusText() {
      return this.isSignedOff && this.isApproved && this.isVerified ? signoffStatusText.SIGNEDOFF : signoffStatusText.PENDING;
    },

    reviewedStatusText() {
      return this.isReviewed ? reviewStatusText.REVIEWED : reviewStatusText.UNREVIEWED;
    },

    lockedStatusText() {
      return this.isLocked ? lockStatusText.LOCKED : lockStatusText.UNLOCKED;
    },

    loggedInUserName() {
      return `${this.getUserDetails("firstName")} ${this.getUserDetails("lastName")}`;
    },

    verifiedStatusEnabled() {
      return this.participantStatuses && this.participantStatuses.verifyStatusEnabled;
    },

    approvedStatusEnabled() {
      return this.participantStatuses && this.participantStatuses.approveStatusEnabled;
    },

    signedOffStatusEnabled() {
      return this.participantStatuses && this.participantStatuses.signoffStatusEnabled;
    },

    reviewedStatusEnabled() {
      return this.participantStatuses && this.participantStatuses.reviewStatusEnabled;
    },

    lockedStatusEnabled() {
      return this.participantStatuses && this.participantStatuses.lockStatusEnabled;
    }
  },

  methods: {
    async getFormStatuses(studyId, participantId, data = {}) {
      try {
        this.loading = true;
        let participantFormStatuses = null;
        const milestoneDay = data.milestone;
        if (this.selectedMilestoneId) {
          // Get Milestone Level Participant Facing Tasks statuses
          participantFormStatuses = await this.$api.getParticipantFacingFormStatusesAtMilestone(studyId, participantId, this.selectedMilestoneId, milestoneDay);
        } else {
          // Get Participant Level Participant-Facing Tasks statuses
          participantFormStatuses = await this.$api.getParticipantFacingFormStatuses(studyId, participantId);
        }

        participantFormStatuses = participantFormStatuses.body.data;

        if (participantFormStatuses) {
          this.setParticipantSiteStatuses(participantFormStatuses);
        }
      } catch (error) {
        if (error && error.body && error.body.message) {
          this.$toastr.error(error.body.message);
        }
      } finally {
        this.loading = false;
      }
    },

    calculateStatusValue(participantFormStatuses) {
      this.setParticipantSiteStatuses(participantFormStatuses);
    },

    setParticipantSiteStatuses(statuses) {
      const approve = statuses.approveStatus === approveStatusNapi.APPROVED ? approvalStatus.APPROVED : approvalStatus.NOT_APPROVED;

      const verify = statuses.verifyStatus === verifiedStatusNapi.VERIFIED ? verificationStatus.VERIFIED : verificationStatus.NOT_VERIFIED;

      const locked = statuses.lockStatus === lockedStatusNapi.LOCKED ? lockStatus.LOCKED : lockStatus.UNLOCKED;

      const review = statuses.reviewStatus === reviewStatusNapi.REVIEWED ? reviewStatus.REVIEWED : reviewStatus.UNREVIEWED;

      const signedOff = statuses.signoffStatus === signoffStatusNapi.SIGNEDOFF ? signoffStatus.SIGNEDOFF : signoffStatus.PENDING;

      this.participantStatuses = {
        approveStatus: approve,
        approveStatusEnabled: statuses.approveEnable,
        lockStatus: locked,
        lockStatusEnabled: statuses.lockEnable,
        reviewStatus: review,
        reviewStatusEnabled: statuses.reviewEnable,
        signoffStatus: signedOff,
        signoffStatusEnabled: statuses.signoffEnable,
        verifyStatus: verify,
        verifyStatusEnabled: statuses.verifyEnable
      };
    },

    calculateStatusesFromSiteAndParticipant(siteFormStatuses, participantFormStatuses) {
      const approveStatus = siteFormStatuses.approveStatus === approvalStatus.APPROVED && participantFormStatuses.approveStatus === approveStatusNapi.APPROVED ? approvalStatus.APPROVED : approvalStatus.NOT_APPROVED;

      const lockedStatus = siteFormStatuses.lockStatus === lockStatus.LOCKED && participantFormStatuses.lockStatus === lockedStatusNapi.LOCKED ? lockStatus.LOCKED : lockStatus.UNLOCKED;

      const reviewedStatus = siteFormStatuses.reviewStatus === reviewStatus.REVIEWED && participantFormStatuses.reviewStatus === reviewStatusNapi.REVIEWED ? reviewStatus.REVIEWED : reviewStatus.UNREVIEWED;

      const signedoffStatus = siteFormStatuses.signoffStatus === signoffStatus.SIGNEDOFF && participantFormStatuses.signoffStatus === signoffStatusNapi.SIGNEDOFF ? signoffStatus.SIGNEDOFF : signoffStatus.PENDING;

      const verifiedStatus = siteFormStatuses.verifyStatus === verificationStatus.VERIFIED && participantFormStatuses.verifyStatus === verifiedStatusNapi.VERIFIED ? verificationStatus.VERIFIED : verificationStatus.NOT_VERIFIED;

      this.participantStatuses = {
        approveStatus: approveStatus,
        lockStatus: lockedStatus,
        reviewStatus: reviewedStatus,
        signoffStatus: signedoffStatus,
        verifyStatus: verifiedStatus
      };
    },

    async updateFormStatus(studyId, participantId, status, participantStatus, data = {}, payload = {}) {
      try {
        await this.$api.updateSiteFacingFormStatuses(studyId, participantId, status, data);

        if (this.selectedMilestoneId) {
          // Update status of Milestone level paticipant-facing tasks
          await this.$api.updateParticipantFacingFormStatusesAtMilestone(studyId, participantId, this.selectedMilestoneId, participantStatus, payload);
        } else {
          // Update status of Participant level paticipant-facing tasks
          await this.$api.updateParticipantFacingFormStatuses(studyId, participantId, participantStatus, payload);
        }
      } catch (error) {
        if (error && error.body && error.body.message) {
          throw new Error(error.body.message);
        }
      }
    },

    async updateApprovalStatus(approved, studyId, participantId, data = {}, userDetails) {
      try {
        this.setUserDetails(userDetails);
        const siteFormStatus = approved ? approvalStatus.APPROVED : approvalStatus.NOT_APPROVED;
        const message = approved ? "Approval confirmed." : "Unapproval confirmed.";
        const participantFormStatus = approveStatusNapi[siteFormStatus];
        const payload = { approvedBy: this.loggedInUserName };

        await this.updateFormStatus(studyId, participantId, siteFormStatus, participantFormStatus, data, payload);

        await this.getFormStatuses(studyId, participantId, data);

        this.$toastr.success(message);
        this.$emit("statusUpdated", { milestone: this.milestone, siteFormStatus: participantFormStatus, approvedFlage: approved });
      } catch (e) {
        this.$toastr.error(e);
      }
    },

    async updateVerifyStatus(verify, studyId, participantId, data = {}, userDetails) {
      try {
        this.setUserDetails(userDetails);
        const siteFormStatus = verify ? verificationStatus.VERIFIED : verificationStatus.NOT_VERIFIED;
        const message = verify ? "Verification confirmed." : "Unverification confirmed.";
        const participantFormStatus = verifiedStatusNapi[siteFormStatus];
        const payload = { verifiedBy: this.loggedInUserName };

        await this.updateFormStatus(studyId, participantId, siteFormStatus, participantFormStatus, data, payload);

        await this.getFormStatuses(studyId, participantId, data);

        this.$toastr.success(message);
        this.$emit("statusUpdated",  { milestone: this.milestone, siteFormStatus: participantFormStatus, verifyFlage: verify });
      } catch (e) {
        this.$toastr.error(e);
      }
    },

    async updateSignedOffStatus(signedOff, studyId, participantId, data = {}, userDetails) {
      try {
        this.setUserDetails(userDetails);
        const siteFormStatus = signedOff ? signoffStatus.SIGNEDOFF : signoffStatus.PENDING;
        const message = signedOff ? "Signed off confirmed." : "Unsigned Off confirmed.";
        const participantFormStatus = signoffStatusNapi[siteFormStatus];
        const payload = { signoffBy: this.loggedInUserName };

        await this.updateFormStatus(studyId, participantId, siteFormStatus, participantFormStatus, data, payload);

        await this.getFormStatuses(studyId, participantId, data);

        this.$toastr.success(message);
        this.$emit("statusUpdated",  { milestone: this.milestone, siteFormStatus: participantFormStatus, signedOffFlage: signedOff });
      } catch (e) {
        this.$toastr.error(e);
      }
    },

    async updateReviewStatus(reviewed, studyId, participantId, data = {}, userDetails) {
      try {
        this.setUserDetails(userDetails);
        const siteFormStatus = reviewed ? reviewStatus.REVIEWED : reviewStatus.UNREVIEWED;
        const message = reviewed ? "Reviewed confirmed." : "Unreviewed Confirmed.";
        const participantFormStatus = reviewStatusNapi[siteFormStatus];
        const payload = { reviewedBy: this.loggedInUserName };

        await this.updateFormStatus(studyId, participantId, siteFormStatus, participantFormStatus, data, payload);

        await this.getFormStatuses(studyId, participantId, data);

        this.$toastr.success(message);
        this.$emit("statusUpdated",  { milestone: this.milestone, siteFormStatus: participantFormStatus, reviewedFlage: reviewed });
      } catch (e) {
        this.$toastr.error(e);
      }
    },

    async updateLockStatus(locked, studyId, participantId, data = {}, userDetails) {
      try {
        this.setUserDetails(userDetails);
        const siteFormStatus = locked ? lockStatus.LOCKED : lockStatus.UNLOCKED;
        const message = locked ? "Locked confirmed." : "Unlocked confirmed.";
        const participantFormStatus = lockedStatusNapi[siteFormStatus];
        const payload = { lockedBy: this.loggedInUserName };

        await this.updateFormStatus(studyId, participantId, siteFormStatus, participantFormStatus, data, payload);

        await this.getFormStatuses(studyId, participantId, data);

        this.$toastr.success(message);
        this.$emit("statusUpdated",   { milestone: this.milestone, siteFormStatus: participantFormStatus, lockedFlage: locked });
      } catch (e) {
        this.$toastr.error(e);
      }
    },

    setUserDetails(userDetails) {
      this.userDetails = userDetails;
    },

    getUserDetails(userData) {
      if (this.user && this.user[userData]) {
        return this.user[userData];
      } else if (this.userDetails && this.userDetails[userData]) {
        return this.userDetails[userData];
      } else {
        return "";
      }
    }
  }
};
